// Bet Cracker — anime.js v4 motion engine
//
// Centralised wrapper around the v4 API. Uses the new primitives:
//   • createSpring()  → physics-based overshoot (used by liquid bars)
//   • createTimeline()→ orchestrated multi-step reveals
//   • stagger(grid:)  → 2-D grid waves (heatmap)
//   • svg.morphTo()   → blade-slash page transitions (CSS clip-path here)
//
// Every helper degrades gracefully under prefers-reduced-motion.

import {
  animate, createTimeline, stagger, utils, createSpring,
} from "animejs";

const REDUCED = typeof window !== "undefined"
  && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

// ─── Numeric counter (with optional ka-chunk finisher) ──────────────────
export function countTo(el, to, opts = {}) {
  if (!el) return null;
  const {
    from = 0, duration = 900, decimals = 0, suffix = "",
    prefix = "", ease = "outQuart", delay = 0, kachunk = false, glowColor,
  } = opts;
  const target = { v: from };
  if (REDUCED) {
    el.textContent = `${prefix}${(+to).toFixed(decimals)}${suffix}`;
    return null;
  }
  const inst = animate(target, {
    v: +to, duration, delay, ease,
    onUpdate: () => {
      el.textContent = `${prefix}${target.v.toFixed(decimals)}${suffix}`;
    },
    onComplete: () => {
      if (!kachunk) return;
      // Tiny shake + neon flash ("ka-chunk" feedback)
      const color = glowColor || "rgba(245,158,11,0.85)";
      animate(el, {
        translateX: [
          { to: -2, duration: 40 }, { to: 2, duration: 40 },
          { to: -1, duration: 40 }, { to: 0, duration: 40 },
        ],
        textShadow: [
          { to: `0 0 14px ${color}`, duration: 80 },
          { to: "0 0 0 transparent", duration: 280 },
        ],
        ease: "outQuad",
      });
    },
  });
  return inst;
}

// ─── Stagger reveal ─────────────────────────────────────────────────────
export function staggerReveal(selector, opts = {}) {
  if (REDUCED) {
    const els = typeof selector === "string"
      ? document.querySelectorAll(selector) : selector;
    els.forEach?.((el) => { el.style.opacity = 1; el.style.transform = "none"; });
    return null;
  }
  const {
    from = 14, scale = 0.985, duration = 540,
    delayBetween = 55, ease = "out(3)", baseDelay = 60,
  } = opts;
  return animate(selector, {
    opacity: [0, 1],
    translateY: [from, 0],
    scale: [scale, 1],
    duration, ease,
    delay: stagger(delayBetween, { start: baseDelay }),
  });
}

// ─── Magnetic hover ─────────────────────────────────────────────────────
export function magnetic(el, opts = {}) {
  if (!el || REDUCED) return () => {};
  const { strength = 0.18, max = 16, returnDuration = 480 } = opts;
  const onMove = (e) => {
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = utils.clamp((e.clientX - cx) * strength, -max, max);
    const dy = utils.clamp((e.clientY - cy) * strength, -max, max);
    animate(el, { translateX: dx, translateY: dy, duration: 220, ease: "outQuad" });
  };
  const onLeave = () => animate(el, {
    translateX: 0, translateY: 0,
    duration: returnDuration, ease: "out(4)",
  });
  el.addEventListener("mousemove", onMove);
  el.addEventListener("mouseleave", onLeave);
  return () => {
    el.removeEventListener("mousemove", onMove);
    el.removeEventListener("mouseleave", onLeave);
  };
}

// ─── 3-D tilt on cards (cursor-driven) ──────────────────────────────────
export function tilt3D(el, opts = {}) {
  if (!el || REDUCED) return () => {};
  const { max = 8, perspective = 800, scaleHover = 1.015 } = opts;
  el.style.transformStyle = "preserve-3d";
  el.style.transition = "box-shadow 220ms ease";
  const parent = el.parentElement;
  if (parent) parent.style.perspective = `${perspective}px`;
  const onMove = (e) => {
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;   // 0..1
    const py = (e.clientY - r.top) / r.height;   // 0..1
    const rx = utils.clamp((0.5 - py) * max * 2, -max, max);
    const ry = utils.clamp((px - 0.5) * max * 2, -max, max);
    animate(el, {
      rotateX: rx, rotateY: ry, scale: scaleHover,
      duration: 180, ease: "outQuad",
    });
    el.style.boxShadow = `${-ry * 1.5}px ${rx * 1.5}px 32px rgba(0,0,0,0.55)`;
  };
  const onLeave = () => {
    animate(el, {
      rotateX: 0, rotateY: 0, scale: 1,
      duration: 520, ease: createSpring({ stiffness: 110, damping: 12 }),
    });
    el.style.boxShadow = "";
  };
  el.addEventListener("mousemove", onMove);
  el.addEventListener("mouseleave", onLeave);
  return () => {
    el.removeEventListener("mousemove", onMove);
    el.removeEventListener("mouseleave", onLeave);
  };
}

// ─── Spring-fill probability bar ───────────────────────────────────────
// Animates a child ``.fill`` element's width with elastic overshoot,
// then sends a "shine" sweep across it. Returns the fill timeline.
export function springFill(barEl, percent, opts = {}) {
  if (!barEl) return null;
  const fill = barEl.querySelector(".bc-fill");
  const shine = barEl.querySelector(".bc-shine");
  if (!fill) return null;
  if (REDUCED) {
    fill.style.width = `${percent}%`;
    return null;
  }
  const { stiffness = 90, damping = 11, delay = 0 } = opts;
  const tl = createTimeline({ defaults: { ease: "outQuad" } })
    .add(fill, {
      width: [`0%`, `${percent}%`],
      duration: 950, delay,
      ease: createSpring({ stiffness, damping, velocity: 6 }),
    });
  if (shine) {
    tl.add(shine, {
      translateX: ["-120%", "260%"],
      opacity: [{ to: 0, duration: 0 }, { to: 0.85, duration: 200 }, { to: 0, duration: 400 }],
      duration: 700, easing: "outQuad",
    }, "-=350");
  }
  return tl;
}

// ─── Aura pulse (Target Kitano breathing glow) ─────────────────────────
export function auraPulse(el, opts = {}) {
  if (!el || REDUCED) return null;
  const {
    color = "rgba(245,158,11,0.55)", offColor = "rgba(245,158,11,0)",
    intensity = 30, duration = 2400, scaleAmp = 1.005,
  } = opts;
  return animate(el, {
    boxShadow: [
      `0 0 0px 0 ${offColor}`,
      `0 0 ${intensity}px 4px ${color}`,
      `0 0 0px 0 ${offColor}`,
    ],
    scale: [1, scaleAmp, 1],
    duration, ease: "inOutSine", loop: true,
  });
}

// ─── Letter-by-letter text reveal ──────────────────────────────────────
export function drawText(el, text, opts = {}) {
  if (!el) return null;
  const { duration = 60, baseDelay = 80, color, glow } = opts;
  el.innerHTML = "";
  Array.from(text).forEach((c) => {
    const span = document.createElement("span");
    span.textContent = c === " " ? "\u00A0" : c;
    span.style.display = "inline-block";
    span.style.opacity = "0";
    span.style.transform = "translateY(8px)";
    if (color) span.style.color = color;
    el.appendChild(span);
  });
  el.style.visibility = "visible";
  if (REDUCED) {
    el.querySelectorAll("span").forEach((s) => {
      s.style.opacity = 1; s.style.transform = "none";
    });
    return null;
  }
  return createTimeline({ defaults: { ease: "out(4)" } })
    .add(el.querySelectorAll("span"), {
      opacity: [0, 1],
      translateY: [8, 0],
      filter: glow
        ? [`drop-shadow(0 0 0 ${glow})`, `drop-shadow(0 0 8px ${glow})`, "drop-shadow(0 0 4px transparent)"]
        : undefined,
      duration: duration * 4,
      delay: stagger(duration, { start: baseDelay }),
    });
}

// ─── Letter blast (dramatic splash) ────────────────────────────────────
// Each character flies in from below with rotation, scale-down from 4x,
// shake jitter, and a neon glow that ripples down the word. Paired with
// `katanaWipe` for the splash → app handoff.
export function letterBlast(el, text, opts = {}) {
  if (!el) return null;
  const { color = "#FAFAFA", glow = "rgba(34,197,94,0.85)" } = opts;
  el.innerHTML = "";
  el.style.display = "inline-flex";
  el.style.visibility = "visible";  // wrapper may have been hidden in JSX
  Array.from(text).forEach((c) => {
    const span = document.createElement("span");
    span.textContent = c === " " ? "\u00A0" : c;
    span.style.display = "inline-block";
    span.style.opacity = "0";
    span.style.color = color;
    span.style.willChange = "transform, opacity, filter, text-shadow";
    el.appendChild(span);
  });
  if (REDUCED) {
    el.querySelectorAll("span").forEach((s) => { s.style.opacity = 1; });
    return null;
  }
  const spans = el.querySelectorAll("span");
  return createTimeline({ defaults: { ease: "outExpo" } })
    .add(spans, {
      opacity: [0, 1],
      translateY: [180, 0],
      scale: [4, 1],
      rotate: [() => utils.random(-90, 90, 0), 0],
      filter: ["blur(12px)", "blur(0px)"],
      textShadow: [
        { to: `0 0 0px ${glow}`, duration: 0 },
        { to: `0 0 26px ${glow}`, duration: 280 },
        { to: `0 0 8px ${glow}`, duration: 320 },
      ],
      duration: 700,
      ease: createSpring({ stiffness: 80, damping: 9 }),
      delay: stagger(85, { start: 60 }),
    })
    .add(spans, {  // Final settle: subtle hop
      translateY: [{ to: -6, duration: 160 }, { to: 0, duration: 240 }],
      ease: "outQuad",
      delay: stagger(20),
    }, "-=180");
}

// ─── Katana slash wipe (overlay clip-path animation) ───────────────────
// Mounts a temporary overlay that "slices" diagonally across the screen.
// Returns the timeline; auto-removes the overlay on complete.
export function katanaWipe(opts = {}) {
  if (REDUCED) return null;
  const { color = "#F59E0B", duration = 720 } = opts;
  const overlay = document.createElement("div");
  overlay.setAttribute("aria-hidden", "true");
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 9998;
    pointer-events: none; will-change: clip-path, opacity;
    background: linear-gradient(135deg,
      transparent 0%, transparent 48%,
      ${color} 49.5%, #FFFFFF 50%, ${color} 50.5%,
      transparent 52%, transparent 100%);
    box-shadow: 0 0 60px ${color};
    mix-blend-mode: screen;
    clip-path: polygon(0 0, 0 0, 0 0, 0 0);
  `;
  document.body.appendChild(overlay);
  return createTimeline({
    defaults: { ease: "inOutQuad" },
    onComplete: () => overlay.remove(),
  })
    .add(overlay, {
      clipPath: [
        "polygon(0 0, 0 0, 0 0, 0 0)",
        "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
      ],
      duration: duration * 0.55,
    })
    .add(overlay, {
      opacity: [{ to: 1, duration: 0 }, { to: 0, duration: duration * 0.45 }],
    }, `-=${Math.round(duration * 0.15)}`);
}

// ─── Heatmap wave (2D grid stagger) ────────────────────────────────────
// Lights up cells in a diagonal wave from corner to corner.
export function heatmapWave(cells, opts = {}) {
  if (REDUCED) return null;
  const { gridCols = 5, gridRows = 5, color = "rgba(245,158,11,0.65)" } = opts;
  return animate(cells, {
    opacity: [0, 1],
    scale: [0.6, 1],
    boxShadow: [
      { to: `0 0 0 transparent`, duration: 0 },
      { to: `0 0 12px ${color}`, duration: 280 },
      { to: `0 0 0 transparent`, duration: 380 },
    ],
    duration: 540,
    ease: "outBack",
    delay: stagger(60, { grid: [gridCols, gridRows], from: "first" }),
  });
}

// ─── Tab slider underline (iOS-style) ──────────────────────────────────
// Moves an absolute-positioned highlight under the active tab.
export function tabSlide(highlightEl, targetTabEl) {
  if (!highlightEl || !targetTabEl) return null;
  const r = targetTabEl.getBoundingClientRect();
  const parent = highlightEl.parentElement.getBoundingClientRect();
  const x = r.left - parent.left;
  const w = r.width;
  if (REDUCED) {
    highlightEl.style.transform = `translateX(${x}px)`;
    highlightEl.style.width = `${w}px`;
    return null;
  }
  return animate(highlightEl, {
    translateX: x,
    width: w,
    duration: 420,
    ease: createSpring({ stiffness: 180, damping: 16 }),
  });
}

// ─── Fly-to-target (parabolic projectile) ──────────────────────────────
// Spawns a small element from `fromEl`, animates it along an arc to
// `toEl`, then removes it. Used for "add to bet slip" coin satisfaction.
export function flyToTarget(fromEl, toEl, opts = {}) {
  if (!fromEl || !toEl || REDUCED) return null;
  const { content = "🪙", size = 22, color = "#F59E0B", duration = 720 } = opts;
  const a = fromEl.getBoundingClientRect();
  const b = toEl.getBoundingClientRect();
  const fromX = a.left + a.width / 2;
  const fromY = a.top + a.height / 2;
  const toX = b.left + b.width / 2;
  const toY = b.top + b.height / 2;

  const proj = document.createElement("div");
  proj.textContent = content;
  proj.style.cssText = `
    position: fixed; left: ${fromX}px; top: ${fromY}px;
    z-index: 9999; pointer-events: none; will-change: transform, opacity;
    font-size: ${size}px; line-height: 1;
    color: ${color}; text-shadow: 0 0 12px ${color};
    transform: translate(-50%, -50%);
  `;
  document.body.appendChild(proj);

  // Mid-arc lift: 80px above the chord midpoint
  const midLift = -(80 + Math.random() * 30);
  return createTimeline({
    defaults: { ease: "outQuad" },
    onComplete: () => proj.remove(),
  })
    .add(proj, {
      // Arc via two segments (rise + fall) using the timeline timestamps
      keyframes: [
        { translateX: 0, translateY: 0, scale: 0.6, opacity: 1, rotate: 0 },
        {
          translateX: (toX - fromX) * 0.5,
          translateY: midLift,
          scale: 1.15, rotate: 200,
        },
        {
          translateX: toX - fromX,
          translateY: toY - fromY,
          scale: 0.5, opacity: 0, rotate: 540,
        },
      ],
      duration,
    });
}
